import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AuthLegalFooter } from '../../auth/components/AuthLegalFooter';
import {
  AuthAlert,
  AuthField,
  AuthInput,
  AuthPageLayout,
  AuthSubmitButton,
  authStyles,
} from '../../auth/components/AuthPageLayout';
import { globalLogin, savePickerSession } from '../services/workspaceService';
import { followWorkspaceSelection } from '../workspaceNavigation';
import styles from './WorkspacePicker.module.css';

/**
 * Sign-in for the global app host. There is no workspace yet, so this only proves who the user is;
 * the session itself is issued later, on the workspace host.
 */
export const GlobalLoginPage: React.FC = () => {
  const { t } = useTranslation(['workspaces', 'auth']);
  const navigate = useNavigate();
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!username.trim()) {
      setError(t('auth:errors.usernameRequired'));
      return;
    }

    if (!password) {
      setError(t('auth:errors.passwordRequired'));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await globalLogin(username.trim(), password);
      savePickerSession({
        token: result.pickerToken,
        expiresAt: result.pickerExpiresAtUtc,
        username: username.trim(),
        workspaces: result.workspaces,
        accountToken: result.accountToken,
      });

      if (result.selection) {
        followWorkspaceSelection(result.selection, navigate);
        return;
      }

      // An account with no workspaces yet skips the empty picker and goes straight to creating one.
      if (result.workspaces.length === 0 && result.accountToken) {
        navigate('/workspaces/new', { replace: true });
        return;
      }

      navigate('/workspaces', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('workspaces:errors.loginFailed'));
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageLayout title={t('workspaces:login.title')} subtitle={t('workspaces:login.subtitle')} footer={<AuthLegalFooter />}>
      <form className={authStyles.form} onSubmit={onSubmit}>
        {error ? <AuthAlert variant="error">{error}</AuthAlert> : null}

        <AuthField label={t('auth:emailLabel')} htmlFor="global-login-email" icon="mail">
          <AuthInput
            id="global-login-email"
            value={username}
            onChange={event => setUsername(event.target.value)}
            autoComplete="username"
            placeholder={t('auth:emailPlaceholder')}
          />
        </AuthField>

        <AuthField label={t('auth:passwordLabel')} htmlFor="global-login-password" icon="lock">
          <AuthInput
            id="global-login-password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={event => setPassword(event.target.value)}
            autoComplete="current-password"
            placeholder={t('auth:passwordPlaceholder')}
            withToggle
            revealed={showPassword}
            onToggleReveal={() => setShowPassword(current => !current)}
            toggleLabel={showPassword ? t('auth:hidePassword') : t('auth:showPassword')}
          />
        </AuthField>

        <AuthSubmitButton loading={isSubmitting} loadingLabel={t('auth:signingIn')}>
          {t('auth:signIn')}
        </AuthSubmitButton>
      </form>

      <p className={styles.footer}>
        <span>{t('workspaces:login.noAccount')}</span>
        <Link className={styles.linkButton} to="/signup">
          {t('workspaces:login.createAccount')}
        </Link>
      </p>
    </AuthPageLayout>
  );
};

export default GlobalLoginPage;
